import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { AuthProvider, RequireAuth } from "./auth";
import { AppShell } from "./components/app-shell";
import { RouteError } from "./components/error-boundary";
import { ToastProvider } from "./components/toast";
import { InboxPage } from "./pages/inbox";
import { LoginPage } from "./pages/login";
import { NotFoundPage } from "./pages/not-found";
import { SignupPage } from "./pages/signup";
// Sailix addition: the agency portal (docs/SAILIX-CHANGES.md).
import { PortalProvider, RequirePortal } from "./portal/auth";
import "./styles.css";

const DashboardPage = lazy(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardPage })));
const CustomersPage = lazy(() => import("./pages/customers").then((module) => ({ default: module.CustomersPage })));
const TeamPage = lazy(() => import("./pages/team").then((module) => ({ default: module.TeamPage })));
const SettingsPage = lazy(() => import("./pages/settings").then((module) => ({ default: module.SettingsPage })));
const SetupPage = lazy(() => import("./pages/setup").then((module) => ({ default: module.SetupPage })));
const RatePage = lazy(() => import("./pages/rate").then((module) => ({ default: module.RatePage })));
const KnowledgeBasePage = lazy(() =>
  import("./pages/knowledge-base").then((module) => ({ default: module.KnowledgeBasePage })),
);
const HelpCenterPage = lazy(() => import("./pages/help-center").then((module) => ({ default: module.HelpCenterPage })));
const ReportsPage = lazy(() => import("./pages/reports").then((module) => ({ default: module.ReportsPage })));
const AutomationsPage = lazy(() =>
  import("./pages/automations").then((module) => ({ default: module.AutomationsPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/forgot-password").then((module) => ({ default: module.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("./pages/reset-password").then((module) => ({ default: module.ResetPasswordPage })),
);
const PortalLayout = lazy(() => import("./portal/layout").then((module) => ({ default: module.PortalLayout })));
const PortalLoginPage = lazy(() => import("./portal/login").then((module) => ({ default: module.PortalLoginPage })));
const PortalTicketsPage = lazy(() => import("./portal/tickets").then((module) => ({ default: module.PortalTicketsPage })));
const PortalNewTicketPage = lazy(() =>
  import("./portal/new-ticket").then((module) => ({ default: module.PortalNewTicketPage })),
);
const PortalThreadPage = lazy(() => import("./portal/thread").then((module) => ({ default: module.PortalThreadPage })));
const PortalHelpPage = lazy(() => import("./portal/help").then((module) => ({ default: module.PortalHelpPage })));
const AcceptInvitePage = lazy(() =>
  import("./pages/accept-invite").then((module) => ({ default: module.AcceptInvitePage })),
);
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 10_000, refetchOnWindowFocus: true, retry: 1 } },
});
const deferred = (element: React.ReactNode) => (
  <Suspense fallback={<div className="route-loading" aria-label="Loading page" />}>{element}</Suspense>
);

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage />, errorElement: <RouteError /> },
  { path: "/signup", element: <SignupPage />, errorElement: <RouteError /> },
  { path: "/forgot-password", element: deferred(<ForgotPasswordPage />), errorElement: <RouteError /> },
  { path: "/reset-password", element: deferred(<ResetPasswordPage />), errorElement: <RouteError /> },
  { path: "/accept-invite", element: deferred(<AcceptInvitePage />), errorElement: <RouteError /> },
  { path: "/help/:workspace/:articleSlug?", element: deferred(<HelpCenterPage />), errorElement: <RouteError /> },
  // Public: customers reach this from a link in their mail client, with no session.
  { path: "/rate/:token", element: deferred(<RatePage />), errorElement: <RouteError /> },
  // Agency portal: its own session cookie, its own API surface (/api/portal).
  { path: "/portal/login", element: deferred(<PortalLoginPage />), errorElement: <RouteError /> },
  {
    path: "/portal",
    element: <RequirePortal />,
    errorElement: <RouteError />,
    children: [
      {
        element: deferred(<PortalLayout />),
        children: [
          { index: true, element: <Navigate to="/portal/tickets" replace /> },
          { path: "tickets", element: deferred(<PortalTicketsPage />) },
          { path: "tickets/new", element: deferred(<PortalNewTicketPage />) },
          { path: "tickets/:ticketId", element: deferred(<PortalThreadPage />) },
          { path: "help/:slug?", element: deferred(<PortalHelpPage />) },
        ],
      },
    ],
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/inbox" replace /> },
          { path: "/dashboard", element: deferred(<DashboardPage />) },
          { path: "/inbox/:ticketId?", element: <InboxPage /> },
          { path: "/tickets", element: <Navigate to="/inbox" replace /> },
          { path: "/customers", element: deferred(<CustomersPage />) },
          { path: "/knowledge-base", element: deferred(<KnowledgeBasePage />) },
          { path: "/reports", element: deferred(<ReportsPage />) },
          { path: "/automations", element: deferred(<AutomationsPage />) },
          { path: "/team", element: deferred(<TeamPage />) },
          { path: "/settings", element: deferred(<SettingsPage />) },
          { path: "/setup", element: deferred(<SetupPage />) },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage />, errorElement: <RouteError /> },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <PortalProvider>
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </ToastProvider>
      </PortalProvider>
    </AuthProvider>
  </StrictMode>,
);
